'use client'

import { navigate } from "./request"
import { Session } from "./types"

const API_URL = process.env.NEXT_PUBLIC_BACKEND_URL

/**
 * @param {Object} options - The request options.
 * @param {string} options.url - The URL to send the request to.
 * @param {string} [options.method] - The HTTP method to use (GET, POST, PUT, DELETE).
 * @param {object} [options.body] - The request body to send (for POST, PUT).
 * @param {boolean} [options.auth] - Whether to include authentication headers.   
 * @param {boolean} [options.cache] - Whether to use cache or not.
 * @param {object} [options.session] - The session object containing user information.
 * @returns {Promise<object|boolean>} - The response data or false if an error occurs.
*/

interface ClientRequest {
  url: string
  method?: string
  body?: object
  auth?: boolean
  cache?: boolean
  session?: Session
  headers?: object
}

export const sendClientRequest = async ({ url, method = 'GET', body = {}, auth = true, cache = false, session, headers: customHeaders = {} }: ClientRequest) => {
  if(!url) return false
  let methodType = ['get', 'post', 'put', 'delete'], accessToken = '', headers = {}, fetchOptions = {}, res
  if(!methodType.includes(method.toLowerCase())) return false
  if(auth){
    if(!session?.user?.id) { navigate('/login'); return false }
    accessToken = session.user.accessToken
    if(!accessToken) { navigate('/login'); return false }
    headers = {"Authorization" : `Bearer ${accessToken}`, "Content-Type": "application/json", "Location": "Storefront", ...customHeaders}
    const vendorId = session.user.vendorId
    if(vendorId) headers = { ...headers, "Vendor": vendorId }
  }
  else headers = {"Content-Type": "application/json", "Location": "Storefront", ...customHeaders}
  if(session?.user?.vendorId) headers = { ...headers, "Vendor": session.user.vendorId }
  if(method.toLowerCase() == 'get' || method.toLowerCase() == 'delete') fetchOptions = { method, headers, credentials: 'include' }
  else fetchOptions = { method, headers, body: JSON.stringify(body), credentials: 'include' }
  if(!cache) fetchOptions = { ...fetchOptions, cache: 'no-store' }
  else fetchOptions = { ...fetchOptions, cache: 'force-cache' }
  try {
    res = await fetch(API_URL + url, fetchOptions)
  } catch(err){
    return false
  }
  if(!res) return false
  const contentType = res.headers.get('content-type')
  if (!contentType || !contentType.includes('application/json')) return false
  const result = await res.json()
  if(!result) return false
  if(result.status == 502) return navigate('/logout')
  return result
}

export const navigateUrl = (url: string, path = '/') => {
  if(!url) return '/'
  if(!path) return url
  if(process.env.NEXT_PUBLIC_ENV == 'production') return url
  const pathnames = path.split('/')
  if(pathnames.length < 2) return url
  const baseUrl = `/${pathnames[1]}`
  if(typeof window !== 'undefined'){
    const clientPath = window.location.pathname
    if(clientPath !== baseUrl && !clientPath.startsWith(`${baseUrl}/`)) return url
  }
  return `${baseUrl}${url}`
}
export const resolveRedirect = (url: string | null, path = '/') => {
  if (!url || !url.startsWith('/') || url.startsWith('//')) return null
  const base = navigateUrl('/', path).replace(/\/$/, '')
  if (base && (url === base || url.startsWith(`${base}/`))) return url
  return navigateUrl(url, path)
}

export const loginHrefFrom = (path = '/') => {
  const base = navigateUrl('/', path).replace(/\/$/, '')
  const here = base && path.startsWith(`${base}/`) ? path.slice(base.length) : base && path === base ? '/' : path
  if (here === '/' || /^\/(login|register|forgot|reset|logout)(\/|$)/.test(here)) return navigateUrl('/login', path)
  return navigateUrl(`/login?url=${encodeURIComponent(here)}`, path)
}
